import { useEffect, useState } from "react";
import api, { apiError, fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import AudioRecorder from "@/components/AudioRecorder";
import { NotebookPen, Paperclip, Send, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

export default function PatientRecords() {
  const [records, setRecords] = useState([]);
  const [text, setText] = useState("");
  const [transcript, setTranscript] = useState("");
  const [file, setFile] = useState(null);
  const [next, setNext] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/records").then((r) => setRecords(r.data));
  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFile(data); toast.success("Anexo pronto");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const save = async () => {
    if (!text && !transcript && !file) { toast.error("Escreva, grave ou anexe algo."); return; }
    setSaving(true);
    try {
      await api.post("/records", {
        content_type: file ? "image" : (transcript ? "audio" : "text"),
        text: text || null, transcript: transcript || null, file_id: file?.id || null,
        add_to_next_session: next,
      });
      toast.success("Registro salvo 💙");
      setText(""); setTranscript(""); setFile(null); setNext(false); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-5 animate-fade-up">
      <div>
        <h1 className="font-heading text-2xl font-bold text-[#1F3B4D] flex items-center gap-2">
          <NotebookPen className="text-[#0F766E]" /> Meu Registro
        </h1>
        <p className="text-sm text-[#94A3B8]">Uma forma rápida de registrar algo importante entre as sessões.</p>
      </div>

      <Card className="rounded-2xl p-5 border-[#E0F2FE] space-y-4">
        <AudioRecorder onComplete={({ transcript: t, file: f }) => { setTranscript((x) => (x ? x + " " : "") + (t || "")); if (f) setFile(f); }} />
        {transcript && (
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-[#0F766E]">Transcrição (revise)</label>
            <Textarea value={transcript} onChange={(e) => setTranscript(e.target.value)} data-testid="record-transcript"
              className="mt-1 rounded-xl border-[#E0F2FE] min-h-[80px]" />
          </div>
        )}
        <Textarea value={text} onChange={(e) => setText(e.target.value)} data-testid="record-text-input"
          placeholder="O que você quer registrar hoje?" className="rounded-xl border-[#E0F2FE] min-h-[100px]" />
        <div className="flex items-center justify-between flex-wrap gap-3">
          <label className="flex items-center gap-2 text-sm text-[#0F766E] cursor-pointer" data-testid="record-attach-label">
            <Paperclip size={15} /> {file ? "Anexo pronto" : "Anexar foto / PDF"}
            <input type="file" className="hidden" accept="image/*,application/pdf" onChange={onUpload} />
          </label>
          <label className="flex items-center gap-2 text-sm text-[#1F3B4D] cursor-pointer">
            <Checkbox checked={next} onCheckedChange={setNext} data-testid="record-next-session-checkbox" />
            Conversar na próxima sessão
          </label>
        </div>
        <Button onClick={save} disabled={saving} data-testid="save-record-button"
          className="w-full rounded-full bg-[#0F766E] hover:bg-[#0d6259] gap-2"><Send size={16} /> Salvar registro</Button>
      </Card>

      <div className="space-y-3">
        <h3 className="font-heading font-bold text-[#1F3B4D]">Registros anteriores</h3>
        {records.map((r) => (
          <Card key={r.id} data-testid={`record-item-${r.id}`} className="rounded-2xl p-4 border-[#E0F2FE]">
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="secondary" className="capitalize text-[11px]">{r.content_type}</Badge>
              <span className="text-xs text-[#94A3B8]">{new Date(r.created_at).toLocaleString("pt-BR")}</span>
            </div>
            <p className="text-sm text-[#1F3B4D]">{r.transcript || r.text}</p>
            {r.file_id && <a href={fileUrl(r.file_id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 mt-2 text-xs px-3 py-1.5 rounded-full bg-[#E0F2FE] text-[#0F766E]"><ImageIcon size={13} /> anexo</a>}
          </Card>
        ))}
        {records.length === 0 && <p className="text-sm text-[#94A3B8]">Nenhum registro ainda.</p>}
      </div>
    </div>
  );
}
